/**
 * API Service Configuration
 * MLM Trading Backend Integration
 */

import axios from 'axios';
import AsyncStorage from '@react-native-async-storage/async-storage';

// Base API URL - Your backend server
export const BASE_URL = 'https://a77.onetradebot.com/api';

// Create axios instance with default config
const apiClient = axios.create({
  baseURL: BASE_URL,
  timeout: 30000, // Increased timeout to 30 seconds
  headers: {
    'Content-Type': 'application/json',
    'Accept': 'application/json',
  },
  // Add retry configuration
  retries: 2,
  retryDelay: 1000,
});

// Request Interceptor
apiClient.interceptors.request.use(
  async (config) => {
    // Add authentication token if available
    const token = await AsyncStorage.getItem('authToken');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    
    console.log('API Request:', {
      method: config.method?.toUpperCase(),
      url: config.url,
      fullUrl: `${config.baseURL}${config.url}`,
      hasToken: !!token,
      headers: config.headers
    });
    
    return config;
  },
  (error) => {
    console.error('Request Setup Error:', error);
    return Promise.reject(error);
  }
);

// Response Interceptor
apiClient.interceptors.response.use(
  (response) => {
    console.log('API Response Success:', response.config.url, response.status);
    return response.data;
  },
  (error) => {
    // Enhanced error handling
    console.log('API Error Details:', {
      url: error.config?.url,
      method: error.config?.method,
      status: error.response?.status,
      message: error.message,
      code: error.code
    });
    
    // Handle network errors
    if (error.code === 'NETWORK_ERROR' || error.message === 'Network Error') {
      console.error('Network Error: Check internet connection and server status');
      error.message = 'Network connection failed. Please check your internet connection.';
    }
    
    // Handle timeout errors
    if (error.code === 'ECONNABORTED') {
      console.error('Request Timeout: Server took too long to respond');
      error.message = 'Request timeout. Please try again.';
    }
    
    // Handle specific error codes
    if (error.response) {
      switch (error.response.status) {
        case 401:
          // Handle unauthorized - redirect to login
          console.log('Unauthorized access - token may be expired');
          AsyncStorage.removeItem('authToken');
          error.message = 'Authentication failed. Please login again.';
          break;
        case 404:
          // Resource not found
          console.log('API endpoint not found:', error.config?.url);
          error.message = 'API endpoint not found.';
          break;
        case 500:
          // Server error
          console.log('Internal server error');
          error.message = 'Server error. Please try again later.';
          break;
        case 503:
          // Service unavailable
          console.log('Service unavailable');
          error.message = 'Service temporarily unavailable.';
          break;
      }
    }
    
    return Promise.reject(error);
  }
);

// ==================== Authentication APIs ====================
// Moved to authApi.js for better organization
export { authAPI } from './authApi';

// ==================== Debug & Test APIs ====================
export const testAPI = {
  // Test network connectivity
  ping: () => apiClient.get('/health').catch(err => {
    console.log('Health check failed:', err.message);
    return { status: 'error', message: err.message };
  }),
  
  // Test authentication
  testAuth: async () => {
    try {
      const token = await AsyncStorage.getItem('authToken');
      if (!token) {
        return { status: 'no-token', message: 'No auth token found' };
      }
      
      const response = await apiClient.get('/user/profile');
      return { status: 'success', message: 'Authentication working' };
    } catch (error) {
      return { status: 'error', message: error.message };
    }
  }
};

// ==================== User APIs ====================
export const userAPI = {
  // Get user's active subscription plan
  getActivePlan: () => apiClient.get('/user/active-plan'),
  
  // Get user's subscription history
  getSubscriptions: () => apiClient.get('/user/subscriptions'),
  
  // Get user wallet balance (force fresh from server)
  getBalance: () => apiClient.get('/user/balance'),
  
  // Get user wallet transactions
  getWalletTransactions: () => apiClient.get('/user/wallet/transactions'),
  
  // Get user wallet history (new endpoint)
  getWalletHistory: () => apiClient.get('/user/wallet/history'),
  
  // Get user debits (commission deductions and withdrawals)
  getDebits: () => apiClient.get('/user/debits'),
  
  // Purchase a subscription plan
  buyPlan: (planId) => apiClient.post('/user/buy-plan', { planId }),
  
  // Purchase subscription (Auto-approves if user has sufficient balance)
  purchaseSubscription: (planId) => apiClient.post('/user/subscriptions/purchase', { planId }),
  
  // Get active subscription
  getActiveSubscription: () => apiClient.get('/user/subscriptions/active'),
  
  // Withdrawal APIs
  createWithdrawal: (withdrawalData) => apiClient.post('/user/withdrawals', withdrawalData),
  getWithdrawals: () => apiClient.get('/user/withdrawals'),
  
  // Referral Withdrawal APIs (specific endpoint for referral withdrawals)
  requestReferralWithdrawal: (withdrawalData) => apiClient.post('/user/referral-withdrawal/request', withdrawalData),
  
  // Get referrer details (who referred this user)
  getReferrer: () => apiClient.get('/user/my-referrer'),
  
  // Get user's referrals (downline)
  getMyReferrals: () => apiClient.get('/user/my-referrals'),
  
  // Get a user by ID (for fetching referrer details as fallback)
  getUserById: (userId) => apiClient.get(`/users/${userId}`),
  
  // Referral Balance APIs
  getReferralBalance: () => apiClient.get('/user/referral-balance'),
};

// ==================== Transactions APIs ====================
export const transactionAPI = {
  // Get user transactions (wallet history)
  getTransactions: (params) => apiClient.get('/transactions', { params }),
};

// ==================== Announcements APIs ====================
export const announcementsAPI = {
  // Get announcements (includes payment details like UPI ID, QR code, etc.)
  getAnnouncements: () => apiClient.get('/announcements'),
};

// ==================== Deposit/Submission APIs ====================
export const depositAPI = {
  // Get user submissions
  getSubmissions: () => apiClient.get('/user/submissions'),
  
  // Submit reply to a submission (with transaction details)
  submitReply: (submissionId, data) => apiClient.post(`/user/submissions/${submissionId}/reply`, data),
};

// ==================== Commissions APIs ====================
export const commissionAPI = {
  // Get commission logs (referral earnings)
  getCommissions: (params) => apiClient.get('/commissions', { params }),
  
  // Get commission history with adminCommission data
  getCommissionHistory: () => apiClient.get('/commissions/history'),
};

// ==================== Referral APIs ====================
export const referralAPI = {
  // Get referral tree for a user
  getReferralTree: (userId) => apiClient.get(`/users/${userId}/tree`),
  
  // Get my referrals with levels and earnings
  getMyReferrals: () => apiClient.get('/user/my-referrals'),
  // Get my earnings and commission breakdown (backend: /referrals/my-earnings)
  getMyEarnings: () => apiClient.get('/referrals/my-earnings'),
  
  // Request referral withdrawal
  requestWithdrawal: (withdrawalData) => apiClient.post('/user/referral-withdrawal/request', withdrawalData),
};

// ==================== User Summary APIs ====================
export const userSummaryAPI = {
  // Get user summary with balance, last transactions, commission data
  getUserSummary: () => apiClient.get('/user/summary'),
};

// ==================== Trades APIs ====================
export const tradesAPI = {
  // Get user trades data (using the specific endpoint for trade transactions)
  getUserTrades: (userId) => apiClient.get(`/admin/user/${userId}`),
};

// ==================== User Trades APIs ====================
export const userTradesAPI = {
  // Get user trades data (new user-specific endpoint)
  // Accept optional params (e.g. { limit: 1000 }) and pass them to the request
  getUserTrades: (params = {}) => apiClient.get('/user/trades', { params }),
};

// ==================== Admin APIs (if user is admin) ====================
export const adminAPI = {
  // Get user transactions (new endpoint)
  getUserTransactions: (userId) => apiClient.get(`/admin/transactions/user/${userId}`),
  
  // Get all plans
  getPlans: () => apiClient.get('/admin/plans'),
  
  // Create a new plan
  createPlan: (planData) => apiClient.post('/admin/plan', planData),
  
  // Update a plan
  updatePlan: (planId, planData) => apiClient.put(`/admin/plan/${planId}`, planData),
  
  // Delete a plan
  deletePlan: (planId) => apiClient.delete(`/admin/plan/${planId}`),
  
  // Get application settings (MLM commission percentages)
  getSettings: () => apiClient.get('/admin/settings'),
  
  // Update application settings
  updateSettings: (settings) => apiClient.put('/admin/settings', settings),
  
  // Get global rate
  getGlobalRate: () => apiClient.get('/admin/global-rate'),
  
  // Set global rate
  setRate: (rateData) => apiClient.post('/admin/set-rate', rateData),
  
  // Apply rate to all users
  applyRate: (rateData) => apiClient.post('/admin/apply-rate', rateData),
  
  // Adjust user balance
  adjustBalance: (balanceData) => apiClient.post('/admin/adjust-balance', balanceData),
  
  // Get all subscriptions
  getSubscriptions: () => apiClient.get('/admin/subscriptions'),
  
  // Get subscription statistics
  getSubscriptionStats: () => apiClient.get('/admin/subscriptions/stats'),
  
  // Get user subscription history
  getUserSubscriptions: (userId) => apiClient.get(`/admin/subscriptions/user/${userId}`),
  
  // Distribute profit with MLM commissions
  distributeProfit: (profitData) => apiClient.post('/admin/profit/distribute', profitData),
  
  // Preview profit distribution
  previewProfit: (profitData) => apiClient.get('/admin/profit/preview', { params: profitData }),
  
  // Get distribution history for a user
  getDistributionHistory: (userId) => apiClient.get(`/admin/profit/history/${userId}`),
  
  // Get MLM settings
  getMLMSettings: () => apiClient.get('/admin/profit/mlm-settings'),
  
  // Create commission tier
  createCommissionTier: (tierData) => apiClient.post('/admin/commission-tier', tierData),
  
  // Get all commission tiers
  getCommissionTiers: () => apiClient.get('/admin/commission-tier'),
  
  // Get admin transactions
  getAdminTransactions: (params) => apiClient.get('/admin/transactions', { params }),
};

// Generic API Methods
export const api = {
  // GET request
  get: (url, config = {}) => apiClient.get(url, config),
  
  // POST request
  post: (url, data, config = {}) => apiClient.post(url, data, config),
  
  // PUT request
  put: (url, data, config = {}) => apiClient.put(url, data, config),
  
  // PATCH request
  patch: (url, data, config = {}) => apiClient.patch(url, data, config),
  
  // DELETE request
  delete: (url, config = {}) => apiClient.delete(url, config),
};

export default api;
